import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { IProviderBreakdownDetails, IProviderBreakdownQualityMeasures, IProviderFilter } from '../../definitions/Providers'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import ActivityManager from '../../managers/ActivityManager'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SelectMultipleBadgeFilter from '../badge_filter/SelectMultipleBadgeFilter'
import BulletChart from '../charts/BulletChart'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import { SmartTableUtilities } from '../controls/table'
import SmartTable from '../controls/table/SmartTable'
import { Row } from '../controls/table/SmartTableDefinitions'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'

export default function ProviderPerformanceBreakdown() {
    const dispatch = useDispatch()
    const params = RoutingManager.getParams<{ contractId: number }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [filter, setFilter] = useState<IProviderFilter>({
        providerIds: []
    })

    const { status, response: providersBreakdown } = useQuery({
        queryKey: buildQueryKey('providers', params.contractId, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getContractProviders(params.contractId, time),
        cache: true
    })

    const { status: statusProviderKPI, response: providerKPI } = useQuery({
        queryKey: buildQueryKey('providers-KPI', params.contractId, TimeHelper.stringify(time), Helper.generateKeyOfObject(filter)),
        queryFn: async () => await ApiClient.getProvidersKPI(params.contractId, time, filter),
        cache: true
    })

    const qualityMeasure = (qm: IProviderBreakdownQualityMeasures) => {
        const { scoringBands, score } = qm
        const isInverse = scoringBands.some(x => x.inverse)
        const first = { name: '', fixedWidth: 10 }
        const bands = isInverse ? [...scoringBands, first] : [first, ...scoringBands]

        return (
            <BulletChart value={score / 100} isInverse={isInverse} bands={bands} />
        )
    }

    const providerRow = (providerDetails: IProviderBreakdownDetails, i: number): Row => {
        return SmartTableUtilities.toRow([
            { value: providerDetails.name,
                onClick: () => dispatch(changeRoute({ route: 'provider', params: { providerId: providerDetails.id, contractId: params.contractId }, customRouteName: providerDetails.name })) },
            { value: providerDetails.gainShare + '%' },
            { value: providerDetails.compositeScore + '%' },
            { value: providerDetails.attributedMembers },
            { value: providerDetails.totalPaymentClaims },
            { value: providerDetails.totalClaims }
        ], i, providerDetails.qualityMeasures?.length > 0 ? providerSubContent(providerDetails.qualityMeasures) : undefined)
    }

    const providerSubContent = (qualityMeasures: IProviderBreakdownQualityMeasures[]) => {
        return (
            <Table>
                <TableBody>
                    {qualityMeasures.map(x => {
                        return (
                            <TableRow key={x.id}>
                                <TableCell width='20%'>
                                    {x.name}
                                </TableCell>
                                <TableCell>
                                    {qualityMeasure(x)}
                                </TableCell>
                                <TableCell width='10%'>
                                    {x.numerator} out of {x.denominator} members
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        )
    }

    const filteredProviders = (providersBreakdown?.providers ?? []).filter(x => filter.providerIds.length === 0 || filter.providerIds.includes(x.id))

    const providersLoading = status === 'loading'
    const kpiLoading = statusProviderKPI === 'loading'

    return (
        <Tile.Container>
            <RouteHeader
                primary='Provider Performance Breakdown'
                timeSettings={time}
                subActions={[{ label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('providers') }]}
            />
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1}>
                    <KPICard
                        label='Quality Measures avg. Scores'
                        isLoading={kpiLoading}
                        unit='%'
                        data={Helper.dataframeToKPICard(providerKPI?.avgScores)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Attributed Members'
                        isLoading={kpiLoading}
                        data={Helper.dataframeToKPICard(providerKPI?.attributedMembers)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Total Claims ($)'
                        isLoading={kpiLoading}
                        inverse
                        unit='$'
                        data={Helper.dataframeToKPICard(providerKPI?.totalPaymentClaims)}
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        label='Total Claims (#)'
                        inverse
                        isLoading={kpiLoading}
                        unit='#'
                        data={Helper.dataframeToKPICard(providerKPI?.totalClaims)}
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={0} spacing={1}>
                <SelectMultipleBadgeFilter
                    name='Providers'
                    isLoading={status === 'loading'}
                    value={filter.providerIds.map(String)}
                    options={providersBreakdown?.providers?.map(x => ({ uuid: x.id.toString(), name: x.name })) ?? []}
                    onApply={x => setFilter(f => ({ ...f, providerIds: x.map(x => Number(x)) }))}
                />
            </Tile.Row>
            <SmartCard style={{ overflowY: 'auto', height: '100%' }}>
                <SmartTable
                    isLoading={providersLoading}
                    sticky
                    columns={[
                        { id: 'provider', label: 'Provider' },
                        { id: 'gain share', label: 'Gain Share', numeric: true },
                        { id: 'compositeScore', label: 'Composite Score', numeric: true },
                        { id: 'attributedMembers', label: 'Attributed Members', numeric: true },
                        { id: 'totalClaims$', label: 'Total Claims ($)', numeric: true },
                        { id: 'totalClaims#', label: 'Total Claims (#)', numeric: true }
                    ]}
                    datasource={{ rows: filteredProviders?.map((x, i) => providerRow(x, i)) }}
                />
            </SmartCard>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
